import React from "react";
import * as Tabs from "@radix-ui/react-tabs";
import BrainTypeView from "./BrainTypeView";
import BrainTypeProbabilities, { formatBrainTypeLabel } from "./BrainTypeProbabilities";
import InterviewCoachPanel from "./InterviewCoachPanel";
import ConflictTestCoachPanel from "./ConflictTestCoachPanel";
import CompetenceCoachPanel from "./CompetenceCoachPanel";
import IQTestCoachPanel from "./IQTestCoachPanel";
import WorkValuesCoachPanel from "./WorkValuesCoachPanel";
import CareerValuesCoachPanel from "./CareerValuesCoachPanel";
import ProfessionTestCoachPanel from "./ProfessionTestCoachPanel";
import { Info } from "@phosphor-icons/react";
import { SubTabTrigger } from "./CoacheeTabTriggers";

/**
 * "Tests"-tab van CoacheeDetailPage. Bevat tot 9 sub-tabs:
 * vastgesteld breintype, online breintype-test (alleen als
 * `survey_enabled` aan staat), IQ-test, werkwaarden, loopbaanwaarden,
 * conflicthanteringstest, competentietest, beroepentest en
 * sollicitatiegesprek-chatbot.
 */
export default function CoacheeTestsPanel({ coachee, coacheeId, coacheeName, surveyResult }) {
  const surveyEnabled = coachee?.survey_enabled !== false;
  return (
    <Tabs.Root defaultValue="brain-type">
      <Tabs.List className="flex flex-wrap gap-1 mb-5 bg-zinc-100 p-1 rounded-sm" data-testid="tabs-tests">
        <SubTabTrigger value="brain-type" testid="subtab-brain-type">Breintype</SubTabTrigger>
        {surveyEnabled && (
          <SubTabTrigger value="online" testid="subtab-online">Online test</SubTabTrigger>
        )}
        <SubTabTrigger value="iq" testid="subtab-iq">IQ-test</SubTabTrigger>
        <SubTabTrigger value="work-values" testid="subtab-work-values">Werkwaarden</SubTabTrigger>
        <SubTabTrigger value="career-values" testid="subtab-career-values">Loopbaanwaarden</SubTabTrigger>
        <SubTabTrigger value="conflict" testid="subtab-conflict">Conflicthantering</SubTabTrigger>
        <SubTabTrigger value="competence" testid="subtab-competence">Competenties</SubTabTrigger>
        <SubTabTrigger value="profession" testid="subtab-profession">Beroepentest</SubTabTrigger>
        <SubTabTrigger value="interview" testid="subtab-interview">Sollicitatiegesprek</SubTabTrigger>
      </Tabs.List>
      <Tabs.Content value="brain-type">
        <BrainTypeView coachee={coachee} surveyResult={surveyResult} isOwn={false} calendlyEmbed="" />
      </Tabs.Content>
      {surveyEnabled && (
        <Tabs.Content value="online">
          <OnlineTab coachee={coachee} surveyResult={surveyResult} />
        </Tabs.Content>
      )}
      <Tabs.Content value="iq">
        <IQTestCoachPanel coacheeId={coacheeId} coacheeName={coacheeName} />
      </Tabs.Content>
      <Tabs.Content value="work-values">
        <WorkValuesCoachPanel coacheeId={coacheeId} coacheeName={coacheeName} />
      </Tabs.Content>
      <Tabs.Content value="career-values">
        <CareerValuesCoachPanel coacheeId={coacheeId} coacheeName={coacheeName} />
      </Tabs.Content>
      <Tabs.Content value="conflict">
        <ConflictTestCoachPanel coacheeId={coacheeId} coacheeName={coacheeName} />
      </Tabs.Content>
      <Tabs.Content value="competence">
        <CompetenceCoachPanel coacheeId={coacheeId} coacheeName={coacheeName} />
      </Tabs.Content>
      <Tabs.Content value="profession">
        <ProfessionTestCoachPanel coacheeId={coacheeId} coacheeName={coacheeName} />
      </Tabs.Content>
      <Tabs.Content value="interview">
        <InterviewCoachPanel coacheeId={coacheeId} coacheeName={coacheeName} />
      </Tabs.Content>
    </Tabs.Root>
  );
}

/**
 * Voorlopige uitslag van de online breintype-test, voordat de coach
 * 'm definitief vaststelt. Toont een fallback-state als de coachee de
 * test nog niet heeft afgerond.
 */
function OnlineTab({ coachee, surveyResult }) {
  if (!surveyResult) {
    return (
      <div className="neuro-card-bold p-6 bg-white max-w-3xl" data-testid="online-not-taken">
        <div className="flex items-start gap-3">
          <Info size={22} className="text-[#1E88E5] shrink-0 mt-1" />
          <div>
            <h3 className="font-display text-xl font-bold tracking-tight">Online test nog niet gedaan</h3>
            <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
              {coachee.name} heeft de online breintype-test nog niet ingevuld. Zodra de test is afgerond
              verschijnt hier de voorlopige uitslag.
            </p>
          </div>
        </div>
      </div>
    );
  }
  const hasProbs = surveyResult.probabilities && Object.keys(surveyResult.probabilities).length > 0;
  return (
    <div className="space-y-8">
      <div className="neuro-card-bold p-6 bg-white max-w-3xl" data-testid="online-result">
        <div className="flex items-start gap-3">
          <Info size={22} className="text-[#1E88E5] shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold tracking-tight">Voorlopige uitslag online test</h3>
            {hasProbs && surveyResult.top_brain_type_id && (
              <div className="mt-4 inline-flex items-center gap-2 border-l-4 border-[#1E88E5] bg-[#1E88E5]/5 px-3 py-2 rounded-r-sm">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Meest waarschijnlijk</span>
                <span className="font-display text-sm font-bold tracking-tight">
                  #{surveyResult.top_brain_type_id} {formatBrainTypeLabel(surveyResult.top_brain_type_id)}
                </span>
              </div>
            )}
            {surveyResult.submitted_at && (
              <div className="mt-3 text-xs text-zinc-400">
                Ingevuld op {new Date(surveyResult.submitted_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </div>
      {hasProbs && (
        <div className="max-w-3xl">
          <BrainTypeProbabilities probabilities={surveyResult.probabilities} topId={surveyResult.top_brain_type_id} />
        </div>
      )}
    </div>
  );
}
